import { apiClient } from './apiClient';
import { ThemeColors } from '../theme/engine';

/**
 * Reading and writing a client's branding as a super admin.
 *
 * Distinct from services/branding.ts, which is the read path the app renders
 * from. Logo and favicon are not here: they are uploaded once, on the client's
 * Settings tab, and used everywhere from there.
 */

export interface BrandingDraft {
  productName: string;
  legalName: string;
  tagline: string;
  copyrightText: string;
  supportEmail: string;
  supportUrl: string;
  termsUrl: string;
  privacyUrl: string;
  colors: ThemeColors;
  fontFamily: string;
}

export interface BrandingAdminResponse {
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  /** Icon uploaded on the Settings tab, for the preview. */
  logo: { key: string; bucket: string; contentType?: string } | null;
  fallbackLogoUrl: string | null;
  branding: Record<string, any> | null;
}

export interface ValidationIssue {
  field: string;
  message: string;
}

export const EMPTY_DRAFT: BrandingDraft = {
  productName: '',
  legalName: '',
  tagline: '',
  copyrightText: '',
  supportEmail: '',
  supportUrl: '',
  termsUrl: '',
  privacyUrl: '',
  colors: {},
  fontFamily: '',
};

/**
 * The colours a super admin sets. Everything else in the UI (hover shades,
 * card, list and tab backgrounds, borders, text on buttons) is calculated from
 * these by the theme engine.
 */
export const COLOR_FIELDS: Array<{ key: keyof ThemeColors; label: string; hint: string }> = [
  { key: 'primary', label: 'Primary', hint: 'Buttons, links, active tabs and highlights.' },
  { key: 'secondary', label: 'Secondary', hint: 'Gradients and accents. Calculated from Primary when empty.' },
  { key: 'background', label: 'Page background', hint: 'Cards, lists and panels are shaded from this.' },
  { key: 'text', label: 'Text', hint: 'Lighter text and borders are shaded from this.' },
  { key: 'header', label: 'Header', hint: 'Top navigation bar. Text on it is picked to stay readable.' },
  { key: 'footer', label: 'Footer', hint: 'Page footer. Text on it is picked to stay readable.' },
];

export const brandingAdminService = {
  // `authenticate: true` is required: the option defaults to off, so without it no
  // Authorization header is sent and the gateway rejects the request.
  get(tenantId: string) {
    return apiClient.get<BrandingAdminResponse>(`/auth/api/tenants/${tenantId}/branding`, {
      authenticate: true,
    });
  },

  update(tenantId: string, draft: BrandingDraft) {
    return apiClient.put<{ message: string }>(`/auth/api/tenants/${tenantId}/branding`, draft, {
      authenticate: true,
    });
  },
};

const THEME_KEYS: Array<keyof ThemeColors> = ['primary', 'secondary', 'background', 'text', 'header', 'footer'];

/** Map a stored branding row onto the form's shape, filling gaps with blanks. */
export function toDraft(row: Record<string, any> | null | undefined): BrandingDraft {
  if (!row) return { ...EMPTY_DRAFT, colors: {} };

  const colors: ThemeColors = {};
  for (const key of THEME_KEYS) {
    const value = row.colors?.[key];
    if (typeof value === 'string' && value) colors[key] = value;
  }

  return {
    productName: row.productName ?? '',
    legalName: row.legalName ?? '',
    tagline: row.tagline ?? '',
    copyrightText: row.copyrightText ?? '',
    supportEmail: row.supportEmail ?? '',
    supportUrl: row.supportUrl ?? '',
    termsUrl: row.termsUrl ?? '',
    privacyUrl: row.privacyUrl ?? '',
    colors,
    fontFamily: row.fontFamily ?? '',
  };
}
